import { wrapMethodsWithLogging } from "../../components/helpers/ErrorHandlingHelpers";
import BaseApiRepository from "./BaseApiRepository";

const BaseRepository = {
    async getAsync(url) {
        return await BaseApiRepository.getAsync(`incident/${url}`);
    },
    async postAsync(url, obj) {
        return await BaseApiRepository.postAsync(`incident/${url}`, obj);
    },
    async putAsync(url, obj = null) {
        return await BaseApiRepository.putAsync(`incident/${url}`, obj);
    },
    async deleteAsync(url) {
        return await BaseApiRepository.deleteAsync(`incident/${url}`);
    },
};

export const IncidentApiRepository = {
    async addAsync(incident) {
        return await BaseRepository.postAsync("add", incident);
    },

    async updateAsync(incidentId, incident) {
        return await BaseRepository.putAsync(`${incidentId}`, incident);
    },

    async getByIdAsync(id) {
        return await BaseRepository.getAsync(`${id}`);
    },

    async deleteAsync(id) {
        return await BaseRepository.deleteAsync(`${id}`);
    },

    async getIncidentTypesAsync() {
        return await BaseRepository.getAsync("incident-types");
    },

    async getFilesByIncidentIdAsync(incidentId) {
        return await BaseRepository.getAsync(`${incidentId}/files`);
    },

    async getFileByIdAsync(incidentId, fileId) {
        return await BaseRepository.getAsync(`${incidentId}/file/${fileId}`);
    },

    async createFileByIncidentIdAsync(incidentId, fileRequest) {
        return await BaseRepository.postAsync(`${incidentId}/file`, fileRequest);
    },

    async deleteFileByIdAsync(incidentId, fileId) {
        return await BaseRepository.deleteAsync(`${incidentId}/file/${fileId}`);
    },

    async updateFileByIdAsync(incidentId, fileId, fileRequest) {
        return await BaseRepository.putAsync(`${incidentId}/file/${fileId}`, fileRequest);
    },

    async getPilotReportAsync(incidentId) {
        return await BaseRepository.getAsync(`${incidentId}/pilot-report`);
    },

    async createPilotReportAsync(incidentId) {
        return await BaseRepository.putAsync(`${incidentId}/pilot-report`);
    },

    async previewIncidentReportAsync(incidentId, input) {
        return await BaseRepository.postAsync(`${incidentId}/preview-report`, input);
    },

    async getReportAsync(incidentId) {
        return await BaseRepository.getAsync(`${incidentId}/report`);
    },

    async createReportAsync(incidentId) {
        return await BaseRepository.putAsync(`${incidentId}/report`);
    },
    async getIncidentReportAsync(incidentId) {
        return await BaseApiRepository.getAsync(`incident/${incidentId}/report`);
    },

    async getImagesByIncidentIdAsync(incidentId) {
        return await BaseRepository.getAsync(`${incidentId}/images`);
    },

    async getImageByIdAsync(incidentId, imageId) {
        return await BaseRepository.getAsync(`${incidentId}/image/${imageId}`);
    },

    async createImageByIncidentIdAsync(incidentId, imageRequest) {
        return await BaseRepository.postAsync(`${incidentId}/image`, imageRequest);
    },

    async deleteImageByIdAsync(incidentId, imageId) {
        return await BaseRepository.deleteAsync(`${incidentId}/image/${imageId}`);
    },

    async updateImageByIdAsync(incidentId, imageId, imageRequest) {
        return await BaseRepository.putAsync(`${incidentId}/image/${imageId}`, imageRequest);
    },

    async getImageReportLevelsAsync() {
        return await BaseRepository.getAsync("image-report-levels");
    },

    async createShipAsync(incidentId, incidentShipInput) {
        return await BaseRepository.postAsync(`${incidentId}/incident-ship`, incidentShipInput);
    },

    async getShipsAsync(incidentId) {
        return await BaseRepository.getAsync(`${incidentId}/incident-ships`);
    },

    async setIncidentCorrectionNeededAsync(incidentId, correctionInput) {
        return await BaseApiRepository.putAsync(`incident/${incidentId}/process/correction`, correctionInput);
    },

    async setIncidentCaseworkerCorrectionAsync(incidentId) {
        return await BaseApiRepository.putAsync(`incident/${incidentId}/process/caseworker/correction`);
    },

    async getLoggedInPersonIncidentPilotAsync(incidentId) {
        return await BaseRepository.getAsync(`${incidentId}/incident-pilot`);
    },

    async getIncidentLocationAsync(incidentShipId) {
        return await BaseRepository.getAsync(`${incidentShipId}/incident-location`);
    },

    // Incident Json
    async getConsequencesAsync(id) {
        return await BaseRepository.getAsync(`${id}/consequences`);
    },

    async saveConsequencesAsync(id, obj) {
        return await BaseRepository.putAsync(`${id}/consequences`, obj);
    },
    async getWeatherConditionsAsync(id) {
        return await BaseRepository.getAsync(`${id}/weather-conditions`);
    },

    async saveWeatherConditionsAsync(id, obj) {
        return await BaseRepository.putAsync(`${id}/weather-conditions`, obj);
    },

    async getCaustationsAsync(id) {
        return await BaseRepository.getAsync(`${id}/causations`);
    },

    async saveCausationsAsync(id, obj) {
        return await BaseRepository.putAsync(`${id}/causations`, obj);
    },

    async getViolationOfRegulationsAsync(id) {
        return await BaseRepository.getAsync(`${id}/violation-of-regulations`);
    },

    async saveViolationOfRegulationsAsync(id, incidentJson) {
        return await BaseRepository.putAsync(`${id}/violation-of-regulations`, incidentJson);
    },

    async searchAsync(incidentSearchQuery) {
        return await BaseRepository.postAsync(`search`, incidentSearchQuery);
    },

    async setToNextStatusAsync(incidentId) {
        return await BaseRepository.putAsync(`${incidentId}/status/next`);
    },

    async setToPreviousStatusAsync(incidentId) {
        return await BaseRepository.putAsync(`${incidentId}/status/previous`);
    },

    async setPilotCompleteAsync(incidentId, obj) {
        return await BaseRepository.putAsync(`${incidentId}/process/pilot/complete`, obj);
    },

    async getTimelineRecordsByIncidentIdAsync(incidentId) {
        return await BaseApiRepository.getAsync(`incident/${incidentId}/timeline-records`);
    },

    async createTimelineRecordByIdAsync(incidentId, recordRequest) {
        return await BaseApiRepository.postAsync(`incident/${incidentId}/timeline-record`, recordRequest);
    },

    async updateTimelineRecordByIdAsync(incidentId, recordId, recordRequest) {
        return await BaseApiRepository.putAsync(`incident/${incidentId}/timeline-record/${recordId}`, recordRequest);
    },

    async deleteTimelineRecordByIdAsync(incidentId, recordId) {
        return await BaseApiRepository.deleteAsync(`incident/${incidentId}/timeline-record/${recordId}`);
    },

    async getIncidentActorsByIncidentIdAsync(incidentId) {
        return await BaseApiRepository.getAsync(`incident/${incidentId}/actors`);
    },

    // Summary
    async getRemarksForSummaryAsync(incidentId) {
        return await BaseApiRepository.getAsync(`incident/${incidentId}/summary-remarks`);
    },

    async getRemarksForShipObservationSummaryAsync(incidentId) {
        return await BaseApiRepository.getAsync(`incident/${incidentId}/ship-observation-summary-remarks`);
    },

    async updateSummaryAsync(incidentId, summary) {
        return await BaseApiRepository.putAsync(`incident/${incidentId}/summary`, summary);
    },

    async updateCourseOfEventsSummaryAsync(incidentId, courseOfEventsSummary) {
        return await BaseApiRepository.putAsync(`incident/${incidentId}/course-of-events-summary`, courseOfEventsSummary);
    },

    async updateCourseOfEventsDescriptionAsync(incidentId, courseOfEventsDescription) {
        return await BaseApiRepository.putAsync(`incident/${incidentId}/course-of-events-description`, courseOfEventsDescription);
    },

    async updateUnderlyingCausesSummaryAsync(incidentId, underlyingCausesSummary) {
        return await BaseApiRepository.putAsync(`incident/${incidentId}/underlying-causes-summary`, underlyingCausesSummary);
    },

    async updateShipObservationDescriptionAsync(incidentId, shipObservationDescription) {
        return await BaseApiRepository.putAsync(`incident/${incidentId}/ship-observation-description`, shipObservationDescription);
    },

    async getImprovementTypesAsync() {
        return await BaseApiRepository.getAsync(`incident/improvement-types`);
    },

    async createImprovementByIncidentIdAsync(incidentId, improvement) {
        return await BaseApiRepository.putAsync(`incident/${incidentId}/improvement`, improvement);
    },

    async getImprovementsAsync(incidentId) {
        return await BaseApiRepository.getAsync(`incident/${incidentId}/improvements`);
    },

    async deleteImprovementByIdAsync(incidentId, improvementId) {
        return await BaseApiRepository.deleteAsync(`incident/${incidentId}/improvement/${improvementId}`);
    },

    async getIncidentCorrectionsAsync() {
        return await BaseApiRepository.getAsync(`incident/pilot-corrections`);
    },

    // Validation Errors
    async getWeatherConditionErrorsAsync(incidentId) {
        return await BaseApiRepository.getAsync(`incident/${incidentId}/weather-conditions/errors`);
    },

    async getCausationErrorsAsync(incidentId) {
        return await BaseApiRepository.getAsync(`incident/${incidentId}/causations/errors`);
    },
};

wrapMethodsWithLogging(IncidentApiRepository);
